import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, TextField, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Tooltip, InputAdornment,
} from '@mui/material';
import { FixedSizeList } from 'react-window';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LabelIcon from '@mui/icons-material/Label';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PrintIcon from '@mui/icons-material/Print';

import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import PurchaseReturnsPrint from './Print';
import ReactDOM from 'react-dom';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444', dangerDark: '#dc2626',
};

const COLS = [
  { label: '#',        flex: '0 0 44px',  align: 'left'  },
  { label: 'Bill Date',flex: '0 0 90px',  align: 'left'  },
  { label: 'Bill No',  flex: '1 1 0',     align: 'left'  },
  { label: 'Supplier', flex: '2 1 0',     align: 'left'  },
  { label: 'Amount',   flex: '0 0 90px',  align: 'right' },
  { label: 'CGST',     flex: '0 0 80px',  align: 'right' },
  { label: 'SGST',     flex: '0 0 80px',  align: 'right' },
  { label: 'IGST',     flex: '0 0 80px',  align: 'right' },
  { label: 'Total',    flex: '0 0 90px',  align: 'right' },
  { label: 'Qty',      flex: '0 0 60px',  align: 'right' },
  { label: '',         flex: '0 0 130px', align: 'right' },
];

const ROW_HEIGHT  = 36;
const LIST_HEIGHT = 500;

const rowBase = {
  display: 'flex', alignItems: 'center', width: '100%',
  boxSizing: 'border-box', borderBottom: `1px solid ${C.cardBorder}`,
};

function ColCell({ col, children, muted, bold }) {
  return (
    <div style={{
      flex: col.flex, minWidth: 0, padding: '0 8px', fontSize: '12px',
      color: muted ? C.muted : C.text, fontWeight: bold ? 600 : 400,
      textAlign: col.align, overflow: 'hidden', whiteSpace: 'nowrap',
      textOverflow: 'ellipsis', boxSizing: 'border-box',
    }}>
      {children}
    </div>
  );
}

function formatDate(dateString) {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
  return new Date(dateString).toLocaleDateString('en-GB', options);
}

const PurchaseReturnRow = React.memo(({ index, style, data: listData }) => {
  const { items, onDelete, onActivate, onPrint, hasEdit, hasDelete } = listData;
  const item = items[index];
  const isEven = index % 2 === 0;

  return (
    <div
      style={{
        ...style, ...rowBase,
        backgroundColor: isEven ? C.card : '#f7fdfc',
        transition: 'background-color 0.15s',
        opacity: item.IsActive ? 1 : 0.55,
      }}
      onMouseEnter={e => { e.currentTarget.style.backgroundColor = C.accentDim; }}
      onMouseLeave={e => { e.currentTarget.style.backgroundColor = isEven ? C.card : '#f7fdfc'; }}
    >
      <ColCell col={COLS[0]} muted>{index + 1}</ColCell>
      <ColCell col={COLS[1]}>{formatDate(item.BillDate)}</ColCell>
      <ColCell col={COLS[2]} bold>{item.BillNumber}</ColCell>
      <ColCell col={COLS[3]}>{item.SupplierName}</ColCell>
      <ColCell col={COLS[4]}>{item.Amount}</ColCell>
      <ColCell col={COLS[5]}>{item.CGSTAmount}</ColCell>
      <ColCell col={COLS[6]}>{item.SGSTAmount}</ColCell>
      <ColCell col={COLS[7]}>{item.IGSTAmount}</ColCell>
      <ColCell col={COLS[8]}><strong style={{ color: '#0D47A1' }}>{item.TotalAmount}</strong></ColCell>
      <ColCell col={COLS[9]}>{item.TotalQuantity}</ColCell>
      <ColCell col={COLS[10]}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '2px', alignItems: 'center' }}>
          <Tooltip title="Print" arrow>
            <button onClick={() => onPrint(item.Id)}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#8b5cf6', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              <PrintIcon style={{ fontSize: 15 }} />
            </button>
          </Tooltip>
          <Tooltip title="Details" arrow>
            <Link to={`/PurchaseReturnDetails/${item.Id}`}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#f59e0b', textDecoration: 'none' }}>
              <InfoIcon style={{ fontSize: 15 }} />
            </Link>
          </Tooltip>
          {hasEdit && item.IsActive && (
            <Tooltip title="Edit" arrow>
              <Link to={`/PurchaseReturnEdit/${item.Id}`}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#3b82f6', textDecoration: 'none' }}>
                <EditIcon style={{ fontSize: 15 }} />
              </Link>
            </Tooltip>
          )}
          {hasDelete && item.IsActive && (
            <Tooltip title="Delete" arrow>
              <button onClick={() => onDelete(item.Id)}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: C.danger, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <DeleteIcon style={{ fontSize: 15 }} />
              </button>
            </Tooltip>
          )}
          {!item.IsActive && (
            <Tooltip title="Activate" arrow>
              <button onClick={() => onActivate(item.Id)}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#22c55e', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <CheckCircleIcon style={{ fontSize: 15 }} />
              </button>
            </Tooltip>
          )}
        </div>
      </ColCell>
    </div>
  );
});

// ── Main component ────────────────────────────────────────────────────────────
function PurchaseReturnIndex() {
  const history  = useHistory();
  const location = useLocation();
  const { isAuthenticated, companyId, permissions, token, logout } = useAuth();

  const hasCreate = permissions.includes('Create Transaction');
  const hasEdit   = permissions.includes('Edit Transaction');
  const hasDelete = permissions.includes('Delete Transaction');

  const getToday     = () => new Date().toISOString().split('T')[0];
  const getMonthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };

  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [fromDate, setFromDate]   = useState(getMonthStart);
  const [toDate, setToDate]       = useState(getToday);
  const [searchQuery, setSearchQuery]       = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const [deleteId, setDeleteId]     = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const debounceRef = useRef(null);
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(val), 200);
  };

  useEffect(() => {
    const shouldReload = localStorage.getItem('shouldReloadCreatePage') === 'true';
    if (shouldReload) { localStorage.removeItem('shouldReloadCreatePage'); window.location.reload(); return; }
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
  }, [isAuthenticated, companyId, token, logout]);

  useEffect(() => {
    const shouldReload = localStorage.getItem('shouldReload') === 'true';
    const from = location.state?.from;
    if (['/PurchaseReturnCreate', '/PurchaseReturnEdit', '/PurchaseReturnDetails'].includes(from) && shouldReload) {
      localStorage.removeItem('shouldReload');
      window.location.reload();
    }
  }, [location]);

  const fetchTableData = async () => {
    setLoading(true);
    setData([]);
    try {
      const res = await fetchData('PurchaseReturnApi', `/PurchaseReturnDetails/${companyId}/${fromDate}/${toDate}`, token);
      if (res.responseCode === '1111') setData(res.response_data);
    } catch (err) {
      console.error('Error fetching purchase returns:', err);
      if (err.response?.status === 401) {
        logout();
        history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    if (!debouncedQuery.trim()) return data;
    const q = debouncedQuery.toLowerCase();
    return data.filter(item =>
      item.BillNumber?.toString().toLowerCase().includes(q) ||
      item.SupplierName?.toString().toLowerCase().includes(q)
    );
  }, [data, debouncedQuery]);

  const handlePrint = async (PurID) => {
    try {
      const response = await fetchData('PurchaseReturnApi', `/PurchaseReturnPrint/${PurID}`, token);
      const labelData = response.response_data;

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
      iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

      ReactDOM.render(
        <PurchaseReturnsPrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
        iframeDocument.body
      );

      iframeDocument.write('</body></html>');
      iframeDocument.close();

      setTimeout(() => {
        iframe.contentWindow.print();
      }, 1000);

    } catch (error) {
      console.error('Error printing Invoice:', error);
    }
  };

  const itemData = useMemo(() => ({
    items: filteredData,
    onDelete: (id) => { setDeleteId(id); setDeleteOpen(true); },
    onActivate: (id) => activateData('PurchaseReturnApi', `${id}`, token).then(fetchTableData).catch(console.error),
    onPrint: handlePrint,
    hasEdit,
    hasDelete,
  }), [filteredData, hasEdit, hasDelete]);

  const closeDelete = useCallback(() => { setDeleteId(null); setDeleteOpen(false); }, []);
  const handleDelete = async () => {
    try {
      await deleteData('PurchaseReturnApi', `${deleteId}`, token);
      closeDelete();
      fetchTableData();
    } catch (err) { console.error('Error deleting:', err); }
  };

  const btnPrimary = {
    fontSize: '11px', fontWeight: 700, textTransform: 'none',
    bgcolor: C.accent, color: '#fff', borderRadius: '7px', px: 1.4, py: 0.4,
    boxShadow: C.shadow, '&:hover': { bgcolor: C.accentDark },
  };
  const btnSecondary = {
    fontSize: '11px', fontWeight: 600, textTransform: 'none',
    color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`,
    borderRadius: '7px', px: 1.4, py: 0.5,
    '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text },
  };
  const btnDanger = {
    fontSize: '11px', fontWeight: 700, textTransform: 'none',
    bgcolor: C.danger, color: '#fff', borderRadius: '7px', px: 1.6, py: 0.5,
    '&:hover': { bgcolor: C.dangerDark },
  };

  return (
    <Box sx={{ bgcolor: C.bg, p: '6px 6px 6px' }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <AssignmentReturnIcon sx={{ fontSize: 16, color: C.accent }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '13px', color: C.text }}>
            Purchase Return
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Date range */}
          <TextField type="date" size="small" value={fromDate} onChange={e => setFromDate(e.target.value)}
            inputProps={{ style: { fontSize: '11px', padding: '4px 6px' } }}
            sx={{ width: 130, '& .MuiOutlinedInput-root': { borderRadius: '7px', height: '28px', bgcolor: C.card, '& fieldset': { borderColor: C.cardBorder } } }} />
          <TextField type="date" size="small" value={toDate} onChange={e => setToDate(e.target.value)}
            inputProps={{ style: { fontSize: '11px', padding: '4px 6px' } }}
            sx={{ width: 130, '& .MuiOutlinedInput-root': { borderRadius: '7px', height: '28px', bgcolor: C.card, '& fieldset': { borderColor: C.cardBorder } } }} />

          {/* Search */}
          <TextField size="small" placeholder="Search bill / supplier…"
            value={searchQuery} onChange={handleSearchChange}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: C.muted }} /></InputAdornment> }}
            sx={{
              width: 200,
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px', fontSize: '12px', height: '28px', bgcolor: C.card,
                '& fieldset': { borderColor: C.cardBorder },
                '&:hover fieldset': { borderColor: C.accentBorder },
                '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
              },
            }} />

          {/* Refresh */}
          <Tooltip title="Refresh" arrow>
            <button onClick={fetchTableData} disabled={loading}
              style={{
                height: '28px', padding: '0 12px', borderRadius: '7px',
                border: `1px solid ${C.cardBorder}`, backgroundColor: C.card,
                color: C.muted, fontWeight: 600, fontSize: '11px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                boxShadow: C.shadow, fontFamily: 'inherit',
              }}>
              {loading
                ? <CircularProgress size={11} sx={{ color: C.muted }} />
                : <RefreshIcon sx={{ fontSize: 13 }} />}
              Refresh
            </button>
          </Tooltip>
          
        </Box>
      </Box>

      {/* ── Table card ── */}
      <Box sx={{ bgcolor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, overflow: 'hidden' }}>
        {/* Card title bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: '12px', py: '7px', borderBottom: `1px solid ${C.cardBorder}`, bgcolor: `${C.accent}0d` }}>
          <LabelIcon sx={{ fontSize: 15, color: C.accent }} />
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            Purchase Return List
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Typography sx={{ fontSize: '10px', color: C.muted }}>
              {filteredData.length} of {data.length} record{data.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: C.accent }} />
          </Box>
        ) : (
          <div style={{ width: '100%' }}>
            {/* Column header */}
            <div style={{ ...rowBase, backgroundColor: '#f8fafa', borderBottom: `2px solid ${C.cardBorder}`, width: '100%' }}>
              {COLS.map((col, i) => (
                <div key={i} style={{
                  flex: col.flex, minWidth: 0, padding: '7px 8px',
                  fontSize: '10px', fontWeight: 700, color: C.muted,
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  textAlign: col.align, boxSizing: 'border-box',
                }}>
                  {col.label}
                </div>
              ))}
            </div>

            {filteredData.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5, color: C.muted, fontSize: '12px' }}>
                {data.length === 0 ? 'Select a date range and click Refresh' : 'No records found'}
              </Box>
            ) : (
              <FixedSizeList
                height={Math.min(LIST_HEIGHT, filteredData.length * ROW_HEIGHT)}
                itemCount={filteredData.length}
                itemSize={ROW_HEIGHT}
                width="100%"
                itemData={itemData}
                overscanCount={8}
              >
                {PurchaseReturnRow}
              </FixedSizeList>
            )}
          </div>
        )}
      </Box>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{ fontSize: '13px', fontWeight: 800, color: C.text, borderBottom: `1px solid ${C.cardBorder}`, pb: 0.5 }}>
          Delete Purchase Return?
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <DialogContentText sx={{ fontSize: '12px', color: C.muted }}>
            Are you sure you want to delete this purchase return record? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 0.8 }}>
          <Button onClick={closeDelete} sx={btnSecondary}>Cancel</Button>
          <Button onClick={handleDelete} sx={btnDanger} startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default PurchaseReturnIndex;



